import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DataRefreshProvider } from "./contexts/DataRefreshContext";
import { ProductivityProvider } from "./contexts/ProductivityContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { Toaster } from "sonner";
import IdleModal from "./components/common/IdleModal";
import LogoutBlockedModal from "./components/common/LogoutBlockedModal";
import TodoNavigationGuard from "./components/common/TodoNavigationGuard";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MyDeskPage from "./pages/MyDeskPage";
import JobsPage from "./pages/JobsPage";
import JobDetailPage from "./pages/JobDetailPage";
import JobCandidatesPage from "./pages/JobCandidatesPage";
import CandidatesPage from "./pages/CandidatesPage";
import CandidateDetailPage from "./pages/CandidateDetailPage";
import ClientsPage from "./pages/ClientsPage";
import RequirementsPage from "./pages/RequirementsPage";
import RequirementDetailPage from "./pages/RequirementDetailPage";
import InterviewsPage from "./pages/InterviewsPage";
import InterviewsPageV2 from "./pages/InterviewsPageV2"; // interviews-v2 (2026-09-07)
import TasksPage from "./pages/TasksPage";
import ManualSelectionsPage from "./pages/ManualSelectionsPage";
import DossierGenerationPage from "./pages/DossierGenerationPage";
import PublicCVLinksPage from "./pages/PublicCVLinksPage";
import MatchesPage from "./pages/MatchesPage";
import MatchDetailPage from "./pages/MatchDetailPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import AIJobSearchPage from "./pages/AIJobSearchPage";
import MoneyAlertsPage from "./pages/MoneyAlertsPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import GuidedTours from "./components/GuidedTours";
import DealCloserPage from "./pages/DealCloserPage";
import HiredCandidatesPage from "./pages/HiredCandidatesPage";
import PublicCVSubmitPage from "./pages/PublicCVSubmitPage";
import MasterDatabasePage from "./pages/MasterDatabasePage";
import PasswordResetPage from "./pages/PasswordResetPage";
import DossierTemplatesAdminPage from "./pages/DossierTemplatesAdminPage";
import CEOPage from "./pages/CEOPage";
import JMImportPage from "./pages/JMImportPage";
import BDEPage from "./pages/BDEPage";
import ActivityFeedPage from "./pages/ActivityFeedPage"; // activity log 2026-09-08
import CoBDEPage from "./pages/CoBDEPage"; // co-bde 2026-09-07
import DataCleanupPage from "./pages/DataCleanupPage"; // cleanup 2026-09-07
import ClosingPage from "./pages/ClosingPage";
import ClientActivityPage from "./pages/ClientActivityPage";
import BgvPage from "./pages/BgvPage";
import AgreementGeneratorPage from "./pages/AgreementGeneratorPage";
import MailPage from "./pages/MailPage";
import InboxPageV3 from "./pages/InboxPageV3"; // mailbox-v3: InboxPage.js stays on disk, unused
import SubmissionsPage from "./pages/SubmissionsPage";

// Client Portal (client-facing logins, Track A / A6)
import ClientLoginPage from "./pages/portal/ClientLoginPage";
import ClientPortalHome from "./pages/portal/ClientPortalHome";

// Assessments + 60-second video introduction, public candidate pages (Track A / A7)
import AssessmentTakePage from "./pages/public/AssessmentTakePage";
import VideoIntroPage from "./pages/public/VideoIntroPage";

// Candidate Portal Pages
import PortalHomePage from "./pages/PortalHomePage";
import PortalOnboardingPage from "./pages/PortalOnboardingPage";
import CandidatePortalPage from "./pages/CandidatePortalPage";
import CandidatePublicProfile from "./pages/CandidatePublicProfile";
import CandidatePublicJobsPage from "./pages/CandidatePublicJobsPage";
import CandidatePortal from "./pages/CandidatePortal";

// Public HR Pipeline Pages
import HRReviewPage from "./pages/HRReviewPage";
import InterviewSchedulePage from "./pages/InterviewSchedulePage";
import InterviewSelectPage from "./pages/InterviewSelectPage";
import HRPipelinePage from "./pages/HRPipelinePage";
import HRSwipeReviewPage from "./pages/HRSwipeReviewPage";
import HROptOutPage from "./pages/HROptOutPage";
import HRReopenPage from "./pages/HRReopenPage";

// HR Job Review (for external job applications)
import HRJobReviewPage from "./pages/HRJobReviewPage";

// Public Dossier
import DossierPage from "./pages/DossierPage";

// Client CV Viewer (for client decisions with approve/reject)
import ClientCVViewerPage from "./pages/ClientCVViewerPage";

// Public CV Viewer (legacy)
import PublicCVViewerPage from "./pages/PublicCVViewerPage";

// Proposal Mailer
import ProposalMailerPage from "./pages/ProposalMailerPage";
import ProposalMailerHistoryPage from "./pages/ProposalMailerHistoryPage";
import ProposalDealsPage from "./pages/ProposalDealsPage";

// Retargeting Page
import RetargetingPage from "./pages/RetargetingPage";

// HR Signup Landing Page
import HRSignupPage from "./pages/HRSignupPage";

// HR Leads Management
import HRLeadsPage from "./pages/HRLeadsPage";

// Google Analytics Loader
import GoogleAnalyticsLoader from "./components/common/GoogleAnalyticsLoader";

// Protected Route Component
const ProtectedRoute = ({ children, minRole }) => {
  const { user, canAccess } = useAuth();

  if (!user) {
    return <Navigate to="/ats/login" replace />;
  }

  if (minRole && !canAccess(minRole)) {
    return <Navigate to="/ats" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <DataRefreshProvider>
          <BrowserRouter>
            <ProductivityProvider>
              {/* Load Google Analytics if configured */}
              <GoogleAnalyticsLoader />
              {/* Guided Tours for new users */}
              <GuidedTours />
              <Routes>
                {/* HR Signup Landing Page (public) */}
                <Route path="/hr-signup" element={<HRSignupPage />} />
                <Route path="/hr-signup/:slug" element={<HRSignupPage />} />

                {/* Candidate Portal Routes (public - root) */}
                <Route path="/" element={<PortalHomePage />} />
                <Route path="/onboarding/:candidateId" element={<PortalOnboardingPage />} />
                <Route path="/onboard/:candidateId" element={<PortalOnboardingPage />} />
                <Route path="/submit-cv" element={<PublicCVSubmitPage />} />

                {/* Candidate Portal (authenticated) */}
                <Route path="/my" element={<CandidatePortal />} />
                <Route path="/my/*" element={<CandidatePortal />} />

                {/* Password Reset Routes */}
                <Route path="/portal/:identifier/reset-password" element={<PasswordResetPage />} />
                <Route path="/ats/reset-password" element={<PasswordResetPage />} />
                <Route path="/:identifier/reset-password" element={<PasswordResetPage />} />

                {/* ATS Admin Login */}
                <Route path="/ats/login" element={<LoginPage />} />

                {/* Public HR Pipeline Routes (no auth required) */}
                <Route path="/hr-review/:token" element={<HRReviewPage />} />
                <Route path="/hr-batch/:token" element={<HRReviewPage />} />
                <Route path="/interview-schedule/:token" element={<InterviewSchedulePage />} />
                <Route path="/interview-select/:token" element={<InterviewSelectPage />} />
                <Route path="/hr-swipe/:token" element={<HRSwipeReviewPage />} />
                <Route path="/hr-optout/:token" element={<HROptOutPage />} />
                <Route path="/hr-reopen/:token" element={<HRReopenPage />} />
                {/* Assessments + video intro (503 from the API until the settings flags are on) */}
                <Route path="/assess/:token" element={<AssessmentTakePage />} />
                <Route path="/video-intro/:token" element={<VideoIntroPage />} />
                <Route path="/verify/:token" element={<HRReviewPage />} />
                <Route path="/reset/:token" element={<PasswordResetPage />} />
                <Route path="/cv/:token" element={<ClientCVViewerPage />} />
                <Route path="/cv-review/:token" element={<PublicCVViewerPage />} />
                <Route path="/dossier/:token" element={<DossierPage />} />
                <Route path="/candidate/:id" element={<CandidateDetailPage />} />
                <Route path="/match/:id" element={<MatchDetailPage />} />

                {/* HR Job Review (External Job Applications) */}
                <Route path="/hr/review/:token" element={<HRJobReviewPage />} />

                {/* ATS Admin Protected Routes */}
                <Route path="/ats" element={<MainLayout />}>
                  <Route path="bgv" element={<BgvPage />} />
                  <Route path="agreements" element={<AgreementGeneratorPage />} />
                  <Route path="mail" element={<MailPage />} />
                  <Route path="inbox" element={<InboxPageV3 />} />
                  <Route path="submissions" element={<SubmissionsPage />} />
                  <Route index element={<DashboardPage />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="my-desk" element={<MyDeskPage />} />
                  {/* Menu collapse Part 2 (6 Sep 2026): hidden pages now redirect to the screen that replaced them */}
                  <Route path="todos" element={<Navigate to="/ats/manual-selections/tasks" replace />} />
                  <Route path="jobs" element={<JobsPage />} />
                  <Route path="jobs/:id" element={<JobDetailPage />} />
                  <Route path="jobs/:id/candidates" element={<JobCandidatesPage />} />
                  <Route path="candidates" element={<CandidatesPage />} />
                  <Route path="candidates/:id" element={<CandidateDetailPage />} />
                  <Route path="companies" element={<Navigate to="/ats/clients" replace />} />
                  <Route path="companies/:id" element={<Navigate to="/ats/clients" replace />} />
                  <Route path="clients" element={<ClientsPage />} />
                  <Route path="requirements" element={<RequirementsPage />} />
                  <Route path="requirements/:id" element={<RequirementDetailPage />} />
                  <Route path="interviews" element={<InterviewsPageV2 />} />
                  <Route path="manual-selections/interviews" element={<InterviewsPage />} />
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="manual-selections/tasks" element={<TasksPage />} />
                  <Route path="client-emails" element={<Navigate to="/ats/submissions" replace />} />
                  <Route path="manual-selections/client-emails" element={<Navigate to="/ats/submissions" replace />} />
                  <Route path="proposal-mailer" element={<ProposalMailerPage />} />
                  <Route path="proposal-mailer/history" element={<ProposalMailerHistoryPage />} />
                  <Route path="proposal-mailer/deals" element={<ProposalDealsPage />} />
                  <Route path="manual-selections" element={<ManualSelectionsPage />} />
                  <Route path="manual-selections/dossiers" element={<DossierGenerationPage />} />
                  <Route path="manual-selections/public-links" element={<PublicCVLinksPage />} />
                  <Route path="manual-selections/audit" element={<Navigate to="/ats/activity" replace />} />
                  <Route path="manual-selections/communications" element={<Navigate to="/ats/submissions" replace />} />
                  <Route path="matches" element={<MatchesPage />} />
                  <Route path="matches/:id" element={<MatchDetailPage />} />
                  <Route path="matching-resumes" element={<Navigate to="/ats/matches" replace />} />
                  <Route path="calendar" element={<Navigate to="/ats/manual-selections/interviews" replace />} />
                  <Route path="verifications" element={<Navigate to="/ats/bgv" replace />} />
                  <Route path="ai-job-search" element={<AIJobSearchPage />} />
                  <Route path="deal-closer" element={<DealCloserPage />} />
                  <Route path="money-alerts" element={<MoneyAlertsPage />} />
                  <Route path="insights" element={<Navigate to="/ats" replace />} />
                  <Route path="mail-threads" element={<Navigate to="/ats/inbox" replace />} />
                  <Route path="help" element={<HelpCenterPage />} />
                  <Route path="hr-pipeline" element={<HRPipelinePage />} />
                  <Route path="hired-candidates" element={<HiredCandidatesPage />} />
                  <Route
                    path="master-database"
                    element={
                      <ProtectedRoute minRole="admin">
                        <MasterDatabasePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="emails" element={<Navigate to="/ats/submissions" replace />} />
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute minRole="admin">
                        <UsersPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* the rebuilt activity log - page built 2 Sep, installed 8 Sep */}
              <Route
                path="activity"
                element={
                  <ProtectedRoute minRole="admin">
                    <ActivityFeedPage />
                  </ProtectedRoute>
                }
              />
                  <Route
                    path="settings"
                    element={
                      <ProtectedRoute minRole="super_admin">
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="dossier-templates"
                    element={
                      <ProtectedRoute minRole="admin">
                        <DossierTemplatesAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="productivity" element={<Navigate to="/ats" replace />} />
                  <Route path="mailer" element={<Navigate to="/ats" replace />} />
                  <Route
                    path="hr-leads"
                    element={
                      <ProtectedRoute minRole="admin">
                        <HRLeadsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="retargeting"
                    element={
                      <ProtectedRoute minRole="admin">
                        <RetargetingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ceo"
                    element={
                      <ProtectedRoute minRole="admin">
                        <CEOPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="jm-import"
                    element={
                      <ProtectedRoute minRole="admin">
                        <JMImportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="bde"
                    element={
                      <ProtectedRoute minRole="admin">
                        <BDEPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* co-bde 2026-09-07 */}
                  <Route
                    path="co-bde"
                    element={
                      <ProtectedRoute minRole="admin">
                        <CoBDEPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* data cleanup 2026-09-07 */}
                  <Route
                    path="data-cleanup"
                    element={
                      <ProtectedRoute minRole="admin">
                        <DataCleanupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="closing"
                    element={
                      <ProtectedRoute minRole="recruiter">
                        <ClosingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="client-activity"
                    element={
                      <ProtectedRoute minRole="recruiter">
                        <ClientActivityPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Fallback - redirect old routes to new /ats routes */}
                <Route path="/login" element={<Navigate to="/ats/login" replace />} />
                <Route path="/dashboard" element={<Navigate to="/ats/dashboard" replace />} />
                <Route path="/candidates" element={<Navigate to="/ats/candidates" replace />} />
                <Route path="/jobs" element={<Navigate to="/ats/jobs" replace />} />
                <Route path="/portal" element={<Navigate to="/" replace />} />
                <Route path="/portal/onboarding/:candidateId" element={<PortalOnboardingPage />} />

                {/* Client Portal (client logins; 503 from the API until client_portal_enabled).
                    Address is /portal/client; /portal/login stays as a redirect so every invite
                    and reset link already emailed out keeps working. */}
                <Route path="/portal/client" element={<ClientLoginPage />} />
                <Route path="/portal/login" element={<Navigate to="/portal/client" replace />} />
                <Route path="/portal/*" element={<ClientPortalHome />} />

                {/* Candidate Public Profile - /:username (MUST BE LAST) */}
                <Route path="/c/:slug/jobs" element={<CandidatePublicJobsPage />} />
                <Route path="/c/:slug" element={<CandidatePublicProfile />} />
                <Route path="/:username" element={<CandidatePublicProfile />} />
              </Routes>
            <IdleModal />
            <LogoutBlockedModal />
            <TodoNavigationGuard />
          </ProductivityProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </DataRefreshProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
